import type { OAuthProvider } from '@doss/common-all'
import { useWorkOSAuthContext, type WorkOSUserInfo } from '../contexts/WorkOSAuthContext'

/**
 * Unified auth state and methods.
 */
type UseAuthReturn = {
  user: WorkOSUserInfo | null | undefined
  isLoading: boolean
  isSignedIn: boolean
  isSigningIn: boolean
  signIn: (email: string, password: string) => Promise<WorkOSUserInfo>
  signOut: () => void
  signInWithOAuth: (
    provider: OAuthProvider,
    options?: { devEmail?: string; state?: string },
  ) => Promise<void>
  error: Error | null
}

/**
 * Unified authentication hook using WorkOS.
 *
 * This hook provides a consistent interface for authentication operations.
 * Components should use this instead of directly importing useWorkOSAuthContext.
 *
 * Features:
 * - Single import for all auth operations
 * - Consistent API
 * - Type-safe
 *
 * @returns Auth state and methods
 *
 * @example
 * const { user, isLoading, isSignedIn, signIn, signOut, error } = useAuth()
 *
 * const handleLogin = async () => {
 *   await signIn(email, password)
 * }
 */
export const useAuth = (): UseAuthReturn => {
  const { user, isLoading, isSignedIn, signIn, signOut, signInWithOAuth, error } =
    useWorkOSAuthContext()

  return {
    user,
    isLoading,
    isSignedIn,
    isSigningIn: isLoading,
    signIn,
    signOut,
    signInWithOAuth,
    error,
  }
}
